import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CompanyCheckoutSessionRequest,
  CompanyCheckoutSessionResponse,
  CompanyBillingPortalSessionResponse,
  CompanyAiTopUpCheckoutSessionRequest,
  CompanyAiTopUpCheckoutSessionResponse,
  CompanyBillingWorkspaceResponse,
  CompanyDetailsResponse,
  CompanyPlansResponse,
  PlatformTrialCompanyListResponse,
  PlatformTrialCompanyRequest,
  PlatformTrialCompanyResponse,
  PlatformTrialCompanyUpdateRequest,
  PlatformTrialCompanyUpdateResponse,
  CompanySupportWorkspaceResponse,
  SupportAccessRequestResponse,
} from '@handover/shared-types';
import { END_POINTS, METHOD } from '../../constants/constant';
import { customAuthBaseQuery } from '../customBaseQuery';
import { DepartureQuestion } from '../../features/departure/types';

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: customAuthBaseQuery,
  tagTypes: ['Company'],
  endpoints: builder => ({
    getCompanyPlans: builder.query<CompanyPlansResponse, void>({
      query: () => ({
        url: END_POINTS.companyPlans,
        method: METHOD.GET,
      }),
    }),
    createCheckoutSession: builder.mutation<
      CompanyCheckoutSessionResponse,
      CompanyCheckoutSessionRequest
    >({
      query: data => ({
        url: END_POINTS.companyCheckoutSession,
        method: METHOD.POST,
        body: data,
      }),
    }),
    createCompanyBillingCheckoutSession: builder.mutation<
      { sessionId: string; url: string | null },
      {
        billingInterval: 'monthly' | 'annual';
        successUrl: string;
        cancelUrl: string;
      }
    >({
      query: body => ({
        url: END_POINTS.companyBillingCheckout,
        method: METHOD.POST,
        body,
      }),
    }),
    confirmCompanyBillingCheckout: builder.mutation<
      { message?: string },
      { checkoutSessionId: string }
    >({
      query: body => ({
        url: END_POINTS.companyBillingCheckoutConfirm,
        method: METHOD.POST,
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    getMyCompany: builder.query<CompanyDetailsResponse, void>({
      query: () => ({
        url: END_POINTS.companyMe,
        method: METHOD.GET,
      }),
      providesTags: ['Company'],
    }),
    getCompanyBillingWorkspace: builder.query<CompanyBillingWorkspaceResponse, void>({
      query: () => ({
        url: END_POINTS.companyBilling,
        method: METHOD.GET,
      }),
      providesTags: ['Company'],
    }),
    getCompanySupportWorkspace: builder.query<CompanySupportWorkspaceResponse, void>({
      query: () => ({
        url: END_POINTS.companySupport,
        method: METHOD.GET,
      }),
      providesTags: ['Company'],
    }),
    createBillingPortalSession: builder.mutation<
      CompanyBillingPortalSessionResponse,
      { returnUrl: string }
    >({
      query: body => ({
        url: END_POINTS.companyBillingPortal,
        method: METHOD.POST,
        body,
      }),
    }),
    createCompanyAiTopUpCheckoutSession: builder.mutation<
      CompanyAiTopUpCheckoutSessionResponse,
      CompanyAiTopUpCheckoutSessionRequest
    >({
      query: body => ({
        url: END_POINTS.companyBillingAiTopUpCheckout,
        method: METHOD.POST,
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    requestSupportAccess: builder.mutation<SupportAccessRequestResponse, { reason: string }>({
      query: body => ({
        url: END_POINTS.companySupportAccessRequest,
        method: METHOD.POST,
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    createPlatformTrialCompany: builder.mutation<
      PlatformTrialCompanyResponse,
      PlatformTrialCompanyRequest
    >({
      query: body => ({
        url: END_POINTS.companyInternalTrial,
        method: METHOD.POST,
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    getPlatformTrialCompanies: builder.query<PlatformTrialCompanyListResponse, void>({
      query: () => ({
        url: END_POINTS.companyInternalTrial,
        method: METHOD.GET,
      }),
      providesTags: ['Company'],
    }),
    updatePlatformTrialCompany: builder.mutation<
      PlatformTrialCompanyUpdateResponse,
      { companyId: string; body: PlatformTrialCompanyUpdateRequest }
    >({
      query: ({ companyId, body }) => ({
        url: END_POINTS.companyInternalTrialDetail.replace(':companyId', companyId),
        method: METHOD.PUT,
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    getQuestionnaireTemplate: builder.query<{ questions: DepartureQuestion[] }, void>({
      query: () => ({ url: END_POINTS.companyQuestionnaireTemplate, method: METHOD.GET }),
      providesTags: ['Company'],
    }),
    updateQuestionnaireTemplate: builder.mutation<
      { message: string; questions: DepartureQuestion[] },
      DepartureQuestion[]
    >({
      query: questions => ({
        url: END_POINTS.companyQuestionnaireTemplate,
        method: METHOD.PUT,
        body: { questions },
      }),
      invalidatesTags: ['Company'],
    }),
    getCompanyAiCostReport: builder.query<
      {
        report: {
          periodStart: string | null;
          periodEnd: string | null;
          callCount: number;
          totalCostUsd: number;
          costByFeature: Record<string, number>;
          totalCreditsCharged: number;
          topUpRevenueCents: number;
          topUpRevenueUsd: number;
          marginUsd: number;
        };
      },
      void
    >({
      query: () => ({ url: END_POINTS.companyBillingAiCostReport, method: METHOD.GET }),
    }),
  }),
});

export const {
  useGetCompanyPlansQuery,
  useCreateCheckoutSessionMutation,
  useCreateCompanyBillingCheckoutSessionMutation,
  useConfirmCompanyBillingCheckoutMutation,
  useGetMyCompanyQuery,
  useGetCompanyBillingWorkspaceQuery,
  useGetCompanySupportWorkspaceQuery,
  useCreateBillingPortalSessionMutation,
  useCreateCompanyAiTopUpCheckoutSessionMutation,
  useRequestSupportAccessMutation,
  useCreatePlatformTrialCompanyMutation,
  useGetPlatformTrialCompaniesQuery,
  useUpdatePlatformTrialCompanyMutation,
  useGetQuestionnaireTemplateQuery,
  useUpdateQuestionnaireTemplateMutation,
  useGetCompanyAiCostReportQuery,
} = companyApi;
